import { createTheme } from '@mui/material';
import { FontType, themePalette } from '@/components/shared/Theme/types';

declare module '@mui/material/styles' {
  interface Palette {
    tertiary: Palette['primary'];
    typography: Palette['primary'];
  }
  interface PaletteOptions {
    tertiary?: PaletteOptions['primary'];
    typography?: PaletteOptions['primary'];
  }
}

const neutralScale = {
  50: '#f8fafc',
  100: '#f1f5f9',
  200: '#e2e8f0',
  300: '#cbd5e1',
  400: '#94a3b8',
  500: '#64748b',
  600: '#475569',
  700: '#334155',
  800: '#1e293b',
  900: '#0f172a',
};

export const baseThemePalette = {
  primary: {
    main: '#0f172a',
    light: '#1e293b',
    dark: '#020617',
    contrastText: '#ffffff',
  },
  secondary: {
    main: '#2563eb',
    light: '#60a5fa',
    dark: '#1d4ed8',
    contrastText: '#ffffff',
  },
  tertiary: {
    main: '#ffffff',
    dark: neutralScale[100],
  },
  typography: {
    main: '#ffffff',
    dark: neutralScale[900],
  },
  background: {
    default: neutralScale[50],
    paper: '#ffffff',
  },
  text: {
    primary: neutralScale[900],
    secondary: neutralScale[600],
  },
  divider: neutralScale[200],
  success: {
    main: '#16a34a',
  },
  warning: {
    main: '#d97706',
  },
  error: {
    main: '#dc2626',
  },
  info: {
    main: '#0284c7',
  },
};

const buildCompatiblePalette = (palette: themePalette) => ({
  ...baseThemePalette,
  ...palette,
  primary: {
    ...baseThemePalette.primary,
    ...(palette.primary || {}),
  },
  secondary: {
    ...baseThemePalette.secondary,
    ...(palette.secondary || {}),
  },
  tertiary: {
    ...baseThemePalette.tertiary,
    ...(palette.tertiary || {}),
  },
  typography: {
    ...baseThemePalette.typography,
    ...(palette.typography || {}),
  },
  background: {
    ...baseThemePalette.background,
    ...(palette.background || {}),
  },
  text: {
    ...baseThemePalette.text,
    ...(palette.text || {}),
  },
  divider: palette.divider || baseThemePalette.divider,
  success: {
    ...baseThemePalette.success,
    ...(palette.success || {}),
  },
  warning: {
    ...baseThemePalette.warning,
    ...(palette.warning || {}),
  },
  error: {
    ...baseThemePalette.error,
    ...(palette.error || {}),
  },
  info: {
    ...baseThemePalette.info,
    ...(palette.info || {}),
  },
});

export const buildTheme = (palette: themePalette, systemFont: FontType) => {
  const { url, format, fontFamily } = systemFont;
  const compatiblePalette = buildCompatiblePalette(palette);
  const fontStack = [
    fontFamily,
    'Inter',
    'Segoe UI',
    'Roboto',
    'Helvetica Neue',
    'Arial',
    'sans-serif',
  ];

  return createTheme({
    palette: compatiblePalette,
    shape: {
      borderRadius: 12,
    },
    typography: {
      fontFamily: fontStack.join(','),
      h1: {
        fontSize: '2.25rem',
        fontWeight: 700,
        lineHeight: 1.2,
      },
      h2: {
        fontSize: '1.875rem',
        fontWeight: 700,
        lineHeight: 1.25,
      },
      h3: {
        fontSize: '1.5rem',
        fontWeight: 700,
        lineHeight: 1.3,
      },
      h4: {
        fontSize: '1.125rem',
        fontWeight: 700,
        lineHeight: 1.35,
      },
      subtitle1: {
        fontSize: '1rem',
        fontWeight: 600,
        lineHeight: 1.45,
      },
      subtitle2: {
        fontSize: '0.875rem',
        fontWeight: 600,
        lineHeight: 1.45,
      },
      body1: {
        fontSize: '0.9375rem',
        lineHeight: 1.6,
      },
      body2: {
        fontSize: '0.875rem',
        lineHeight: 1.55,
      },
      button: {
        fontWeight: 600,
        textTransform: 'none',
        letterSpacing: 0,
      },
      caption: {
        fontSize: '0.75rem',
        lineHeight: 1.45,
      },
    },
    components: {
      MuiFab: {
        styleOverrides: {
          root: {
            backgroundColor: '#ffffff',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: ({ theme }) => ({
            border: `1px solid ${theme.palette.divider}`,
          }),
        },
      },
      MuiCard: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: '14px',
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: '0 10px 30px -24px rgba(15, 23, 42, 0.5)',
            backgroundImage: 'none',
          }),
        },
      },
      MuiCardContent: {
        styleOverrides: {
          root: {
            padding: '1.25rem',
            '&:last-child': {
              paddingBottom: '1.25rem',
            },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: ({ theme }) => ({
            fontSize: '0.875rem',
            fontWeight: 600,
            borderRadius: '10px',
            minWidth: 'unset',
            textTransform: 'none',
            padding: '0.5rem 0.875rem',
            boxShadow: 'none',
            transition: 'all 0.2s ease',
            '&:hover': {
              transform: 'translateY(-1px)',
              boxShadow: '0 10px 20px -16px rgba(15, 23, 42, 0.45)',
            },
            '&.MuiButton-outlined': {
              borderColor: theme.palette.divider,
            },
          }),
          sizeLarge: {
            padding: '0.625rem 1rem',
          },
        },
      },
      MuiTextField: {
        defaultProps: {
          variant: 'outlined',
          size: 'small',
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: '10px',
            backgroundColor: theme.palette.tertiary.main,
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.divider,
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.primary.light,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.secondary.main,
              borderWidth: '1px',
            },
          }),
          input: {
            paddingTop: '10px',
            paddingBottom: '10px',
          },
        },
      },
      MuiAccordionDetails: {
        styleOverrides: {
          root: {
            padding: '0.5rem 1rem 1.5rem',
          },
        },
      },
      MuiTypography: {
        styleOverrides: {
          root: ({ theme, ownerState }) => ({
            ...(ownerState.color === 'tertiary' && {
              color: theme.palette.tertiary.dark,
            }),
          }),
        },
      },
      MuiTabs: {
        styleOverrides: {
          indicator: ({ theme }) => ({
            height: '3px',
            borderRadius: '99px',
            backgroundColor: theme.palette.secondary.main,
          }),
        },
      },
      MuiTab: {
        styleOverrides: {
          root: ({ theme }) => ({
            textTransform: 'none',
            minHeight: 42,
            fontWeight: 600,
            color: theme.palette.text.secondary,
            '&.Mui-selected': {
              color: theme.palette.primary.main,
            },
          }),
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: ({ theme }) => ({
            backgroundColor: theme.palette.primary.main,
            borderRadius: '8px',
            fontSize: '0.75rem',
            padding: '0.5rem 0.625rem',
          }),
        },
      },
      MuiCssBaseline: {
        styleOverrides: `
          @font-face {
            font-family: ${fontFamily};
            src: url(${url}) format(${format});
          }
        `,
      },
      MuiPopover: {
        defaultProps: {
          disableScrollLock: true,
        },
      },
    },
  });
};
